import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './registrationcss/Register.css';
import signupCheckpointImg from '../../images/PersonalInfo_IMG.png';
import logo from '../../images/auravit_logo.png';
import RegistrationProgressBar from '../../components/usercomponents/RegistrationProgressBar';

export default function SignupCheckpoint() {
  const navigate = useNavigate();
  const [selectedAssistance, setSelectedAssistance] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherReasonText, setOtherReasonText] = useState("");
  const [disabilityText, setDisabilityText] = useState("");
  const [errors, setErrors] = useState({ reason: "", assistance: "" });
  const [isSaving, setIsSaving] = useState(false);

  // Hydrate from saved draft when navigating back
  useEffect(() => {
    const draft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    if (draft.assistanceRequired) {
      setSelectedAssistance(draft.assistanceRequired);
    }
    if (Array.isArray(draft.reasonForAssistance) && draft.reasonForAssistance.length > 0) {
      setSelectedReasons(draft.reasonForAssistance);
    }
    if (draft.reasonText) {
      setOtherReasonText(draft.reasonText);
    }
    if (draft.disabilityText) {
      setDisabilityText(draft.disabilityText);
    }
  }, []);

  const assistanceOptions = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  const reasonOptions = [
    { label: "Minor", value: "minor" },
    { label: "Senior / Elderly", value: "senior" },
    { label: "Person With Disability (PWD)", value: "pwd" },
    { label: "No Read, No Write (NRNW)", value: "nrnw" },
    { label: "Others (Please specify)", value: "others" },
  ];

  const hasMinorAndSenior = selectedReasons.includes('minor') && selectedReasons.includes('senior');
  
  const getFlowType = () => {
    if (selectedReasons.includes('minor')) {
      return 'minor'; 
    } else if (selectedReasons.includes('pwd') || selectedReasons.includes('nrnw') || selectedReasons.includes('others')) {
      return 'pwd'; 
    } else if (selectedReasons.includes('senior')) {
      return 'pwd'; 
    }
    return 'none';
  };

  const toggleReason = (value) => {
    if (value === 'minor' && selectedReasons.includes('senior')) {
      setSelectedReasons(['minor']);
    } else if (value === 'senior' && selectedReasons.includes('minor')) {
      setSelectedReasons(['senior']);
    } else {
      setSelectedReasons(prev => {
        if (prev.includes(value)) {
          return prev.filter(reason => reason !== value);
        } else {
          return [...prev, value];
        }
      });
    }
    setErrors(prev => ({ ...prev, reason: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedAssistance) {
      newErrors.assistance = "Please select if you need assistance";
    }

    if (selectedAssistance === "yes" && selectedReasons.length === 0) {
      newErrors.reason = "Please select at least one reason for assistance";
    }

    if (selectedAssistance === "yes" && selectedReasons.includes("others") && !otherReasonText.trim()) {
      newErrors.reason = "Please specify other reason";
    }

    if (selectedAssistance === "yes" && selectedReasons.includes("pwd") && !disabilityText.trim()) {
      newErrors.reason = "Please describe your disability";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validateForm()) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      // Get existing data from localStorage
      const existingData = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
      
      if (selectedAssistance === "no") {
        const signupData = {
          assistanceRequired: "no",
          assistancenotrequired: "no",
          timestamp: new Date().toISOString()
        };
        
        const updatedData = { 
          ...existingData, 
          ...signupData,
          reasonForAssistance: [],
          reasonText: "",
          disabilityText: ""
        };
        localStorage.setItem('registrationDraft', JSON.stringify(updatedData));
        
        navigate('/personal-info', { state: updatedData });
      } else {
        const flowType = getFlowType();
        const signupData = {
          assistanceRequired: selectedAssistance,
          reasonForAssistance: selectedReasons, 
          reasonText: selectedReasons.includes("others") ? otherReasonText : "",
          disabilityText: selectedReasons.includes("pwd") ? disabilityText : "",
          flowType: flowType, 
          timestamp: new Date().toISOString()
        };
        
        const updatedData = { ...existingData, ...signupData };
        localStorage.setItem('registrationDraft', JSON.stringify(updatedData));
        
        navigate('/personal-info', { state: updatedData });
      }
    } catch (error) {
      console.error("Error saving signup data:", error);
      alert("Failed to save your information. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="login-logo-container">
        <img 
          src={logo} 
          alt="AuraVit" 
          className="login-logo"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="register-container">
        <div className="register-card">
          <div className="left-panel">
            <img
              src={signupCheckpointImg}
              alt="Signup Checkpoint"
              className="panel-image"
            />
          </div>
          <div className="right-panel">
            <div className="registration-header-left">
              <h1 className="create-account-title">Create Account</h1>
              <p className="wellness-subtitle">
                Let us know if you need any assistance with your account setup.
              </p>
            </div>

            <RegistrationProgressBar currentStep={1} />

            
            {/* Display submit error if backend save fails */}
            {errors.submit && (
              <div className="alert alert-error" role="alert">
                {errors.submit}
              </div>
            )}

            <form noValidate>
              {/* Assistance Required */}
              <div className="form-row">
                <div className="input-group">
                  <label style={{ position: 'relative', top: '0', left: '0', padding: '0', marginBottom: '10px', fontSize: '14px', fontWeight: '500' }}>
                    Do you require assistance with your account setup? <span className="required">*</span>
                  </label>
                  <div className="form-row" style={{ gap: '15px' }}>
                    {assistanceOptions.map((option) => (
                      <div key={option.value} className="input-group">
                        <button
                          type="button"
                          className={`assistance-toggle-btn ${selectedAssistance === option.value ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedAssistance(option.value);
                            setErrors(prev => ({ ...prev, assistance: "" }));
                            // Reset reason text when assistance changes
                            if (option.value === "no") {
                              setSelectedReasons([]);
                              setOtherReasonText("");
                              setDisabilityText("");
                            }
                          }}
                          disabled={isSaving}
                          style={{
                            width: '100%',
                            height: '50px', // Smaller height
                            textAlign: 'center',
                            padding: '8px 16px',
                            fontSize: '1rem',
                            fontWeight: '500',
                            border: selectedAssistance === option.value ? '2px solid #007bff' : '2px solid #e1e5e9',
                            borderRadius: '8px',
                            backgroundColor: selectedAssistance === option.value ? '#007bff' : '#ffffff',
                            color: selectedAssistance === option.value ? '#ffffff' : '#007bff',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: selectedAssistance === option.value ? '0 2px 4px rgba(0, 123, 255, 0.3)' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSaving && selectedAssistance !== option.value) {
                              e.target.style.backgroundColor = '#f8f9ff';
                              e.target.style.borderColor = '#007bff';
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 123, 255, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSaving && selectedAssistance !== option.value) {
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.borderColor = '#e1e5e9';
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                        >
                          {option.label}
                        </button>
                      </div>
                    ))}
                  </div>
                  {errors.assistance && <div className="field-error">{errors.assistance}</div>}
                </div>
              </div>

              {/* Reason for Assistance - Only show if assistance is needed */}
              {selectedAssistance === "yes" && (
                <div className="form-row">
                  <div className="input-group">
                    <label style={{ position: 'relative', top: '0', left: '0', padding: '0', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Reason for Account Assistance (Select all that apply) <span className="required">*</span>
                    </label>
                    <small className="helper-text" style={{ textAlign: 'center', color: '#999999', marginBottom: '15px', display: 'block' }}>
                      Note: Minor and Senior cannot be selected together
                    </small>
                    {reasonOptions.map((option) => (
                      <div key={option.value} style={{ width: '100%', marginBottom: '20px' }}>
                        <label className="checkbox-option" style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 16px',
                          border: '2px solid #e1e5e9',
                          borderRadius: '8px',
                          backgroundColor: selectedReasons.includes(option.value) ? '#f8f9ff' : '#ffffff',
                          cursor: isSaving ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          width: '100%',
                          position: 'relative', // Ensure relative to avoid overlap
                          top: '0', // Reset top
                          left: '0' // Reset left
                        }}>
                          <input
                            type="checkbox"
                            value={option.value}
                            checked={selectedReasons.includes(option.value)}
                            onChange={() => toggleReason(option.value)}
                            disabled={isSaving}
                            style={{
                              width: '16px',
                              height: '16px',
                              marginRight: '10px',
                              cursor: isSaving ? 'not-allowed' : 'pointer',
                              flexShrink: 0
                            }}
                          />
                          <span className="checkbox-label" style={{
                            fontSize: '0.95rem',
                            color: '#333333',
                            fontWeight: selectedReasons.includes(option.value) ? '500' : '400',
                            lineHeight: '1.2'
                          }}>{option.label}</span>
                        </label>
                        
                        {/* Disability Text - Show directly below PWD checkbox */}
                        {option.value === "pwd" && selectedReasons.includes("pwd") && (
                          <div style={{ marginTop: '10px', marginLeft: '26px' }}>
                            <div className="other-box">
                              <textarea
                                className="other-input"
                                placeholder="Describe your disability for better assistance"
                                value={disabilityText}
                                onChange={(e) => setDisabilityText(e.target.value)}
                                maxLength={50}
                                disabled={isSaving}
                                rows={2}
                              />
                              {/* Live Character Counter */}
                              <div className="char-counter">
                                {disabilityText.length}/50
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Other Reason Text - Show directly below Others checkbox */}
                        {option.value === "others" && selectedReasons.includes("others") && (
                          <div style={{ marginTop: '10px', marginLeft: '26px' }}>
                            <div className="other-box">
                              <textarea
                                className="other-input"
                                placeholder="Enter your reason"
                                value={otherReasonText}
                                onChange={(e) => setOtherReasonText(e.target.value)}
                                maxLength={50}
                                disabled={isSaving}
                                rows={2}
                              />
                              {/* Live Character Counter */}
                              <div className="char-counter">
                                {otherReasonText.length}/50
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {hasMinorAndSenior && (
                      <div className="field-error">Minor and Senior cannot be selected together</div>
                    )}
                    {errors.reason && <div className="field-error">{errors.reason}</div>}
                  </div>
                </div>
              )}

             

              {/* Action Buttons */}
              <div className="form-actions">
                <button
                  type="button"
                  className="continue-btn"
                  onClick={handleContinue}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
